if (!customElements.get('loshen-gift-card')) {
  customElements.define('loshen-gift-card', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.form = this.querySelector('form');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.recipientArea = this.querySelector('[data-recipient-area]');
      this.submitButton = this.querySelector('[data-submit]');
      this.submitLabel = this.querySelector('[data-submit-label]');
      this.idleLabel = this.submitLabel.textContent;
      this.status = this.querySelector('[data-status]');
      this.error = this.querySelector('[data-error-message]');
      this.dateInput = this.querySelector('[data-send-date]');
      this.schedule = this.querySelector('[data-schedule]');
      this.email = this.querySelector('input[type="email"]');

      this.querySelector('[data-amount-buttons]').hidden = false;
      this.querySelector('[data-self-choice]').hidden = false;
      this.querySelector('[data-schedule-control]').hidden = false;

      this.querySelector('[data-timezone]').value =
        String(new Date().getTimezoneOffset());

      const isoDate = date => [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')
      ].join('-');

      const today = new Date();
      const latest = new Date(today);
      latest.setDate(latest.getDate() + 90);

      this.dateInput.min = isoDate(today);
      this.dateInput.max = isoDate(latest);

      this.querySelectorAll('[data-design]').forEach(input => {
        input.addEventListener('change', () => this.updateDesign());
      });

      this.querySelectorAll('[data-variant]').forEach(button => {
        button.addEventListener('click', () => {
          this.variantSelect.value = button.dataset.variant;
          this.variantSelect.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        });
      });

      this.variantSelect.addEventListener('change', () => {
        this.updateVariant(true);
      });

      this.querySelectorAll('[data-delivery]').forEach(input => {
        input.addEventListener('change', () => this.updateDelivery());
      });

      this.schedule.addEventListener('change', () => this.updateDate());
      this.recipientArea.addEventListener('input', () => this.updateNote());
      this.form.addEventListener('submit', event => this.addToCart(event));

      window.addEventListener('pageshow', event => {
        if (!event.persisted) return;

        this.submitting = false;
        this.submitButton.removeAttribute('aria-busy');
        this.submitLabel.textContent = this.idleLabel;
        this.status.hidden = true;
        this.updateVariant(false);
      });

      this.updateDesign();
      this.updateVariant(false);
      this.updateDelivery();
    }

    updateDesign() {
      const selected = this.querySelector('[data-design]:checked');
      if (!selected) return;

      const image = this.querySelector('[data-main-image]');
      image.src = selected.dataset.image;
      image.alt = selected.dataset.alt;

      this.querySelector('[data-design-title]').textContent =
        selected.dataset.label;

      this.querySelector('[data-occasion-label]').value =
        selected.dataset.label;
    }

    updateVariant(updateUrl) {
      const option = this.variantSelect.selectedOptions[0];
      if (!option) return;

      this.querySelectorAll('span[data-money]').forEach(node => {
        node.textContent = option.dataset.money;
      });

      this.querySelectorAll('[data-variant]').forEach(button => {
        button.setAttribute(
          'aria-pressed',
          String(button.dataset.variant === option.value)
        );
      });

      this.submitButton.disabled = option.dataset.available !== 'true';

      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    }

    isRecipient() {
      return this.querySelector('[data-delivery]:checked').value === 'recipient';
    }

    updateDelivery() {
      const recipient = this.isRecipient();

      this.recipientArea.hidden = !recipient;

      this.recipientArea.querySelectorAll('input, textarea').forEach(input => {
        input.disabled = !recipient;
      });

      this.email.required = recipient;
      this.querySelector('[data-self-help]').hidden = recipient;

      this.updateDate();
      this.updateNote();
    }

    updateDate() {
      const active = this.isRecipient() && this.schedule.checked;

      this.querySelector('[data-date-row]').hidden = !active;
      this.dateInput.disabled = !active;
      this.dateInput.required = active;
    }

    updateNote() {
      const name = this.querySelector('[data-recipient-name]').value.trim();
      const sender = this.querySelector('[data-sender]').value.trim();
      const messageInput = this.querySelector('[data-message]');
      const message = messageInput.value.trim();

      this.querySelector('[data-count]').textContent =
        String(messageInput.value.length);

      this.querySelector('[data-note]').hidden =
        !this.isRecipient() || !(name || sender || message);

      const to = this.querySelector('[data-note-to]');
      to.textContent = to.dataset.prefix + ' ' + name;
      to.hidden = !name;

      const body = this.querySelector('[data-note-message]');
      body.textContent = message;
      body.hidden = !message;

      const from = this.querySelector('[data-note-from]');
      from.textContent = from.dataset.prefix + ' ' + sender;
      from.hidden = !sender;
    }

    async addToCart(event) {
      event.preventDefault();

      if (this.submitting || !this.form.reportValidity()) return;

      this.submitting = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitLabel.textContent = this.dataset.loading;
      this.error.hidden = true;
      this.status.hidden = true;

      const data = new FormData(this.form);
      data.delete('lc_delivery');

      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          body: data,
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        const result = await response.json();

        if (!response.ok || result.status >= 400) {
          let detail = result.description || result.message || result.errors;

          if (detail && typeof detail === 'object') {
            detail = Object.values(detail).flat().join(' ');
          }

          throw new Error(
            typeof detail === 'string' ? detail : this.dataset.error
          );
        }

        this.status.textContent = this.dataset.added;
        this.status.hidden = false;

        const cartUrl = new URL(
          this.dataset.cartUrl,
          window.location.origin
        );

        const previewTheme = new URL(
          window.location.href
        ).searchParams.get('preview_theme_id');

        if (previewTheme) {
          cartUrl.searchParams.set('preview_theme_id', previewTheme);
        }

        window.location.assign(cartUrl.href);
      } catch (error) {
        this.error.textContent = error.message || this.dataset.error;
        this.error.hidden = false;
        this.submitting = false;
        this.submitButton.removeAttribute('aria-busy');
        this.submitLabel.textContent = this.idleLabel;
        this.updateVariant(false);
      }
    }
  });
}
/* Keep occasion selection working if an app replaces the controls. */
(() => {
  if (window.loshenGiftOccasionFix) return;
  window.loshenGiftOccasionFix = true;

  function showOccasion(input) {
    const gift = input.closest('loshen-gift-card');
    if (!gift || !input.checked) return;

    const image = gift.querySelector('[data-main-image]');
    const title = gift.querySelector('[data-design-title]');
    const occasion = gift.querySelector('[data-occasion-label]');

    if (image && input.dataset.image) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = input.dataset.image;
      image.alt = input.dataset.alt || '';
    }

    if (title) title.textContent = input.dataset.label || '';
    if (occasion) occasion.value = input.dataset.label || '';
  }

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest(
      'loshen-gift-card .lcgift__design'
    );
    const input = label?.querySelector('input[data-design]');

    if (!input || input.disabled) return;

    if (event.target !== input) {
      event.preventDefault();
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    showOccasion(input);
  }, true);

  document.addEventListener('change', event => {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.matches('loshen-gift-card [data-design]')
    ) {
      showOccasion(event.target);
    }
  }, true);
})();